import React, { useState } from 'react';
import { Shield, ChevronDown, FileText, Users, Scale, AlertTriangle, RefreshCw, Mail, Phone } from 'lucide-react';

interface Section {
  id: string;
  title: string;
  icon: JSX.Element;
  content: string | string[];
}

function TermsOfService() {
  const [expandedSection, setExpandedSection] = useState<string | null>(null);

  const sections: Section[] = [
    {
      id: 'general',
      title: 'Общие положения',
      icon: <Shield className="w-6 h-6" />,
      content: [
        'Настоящие Условия регулируют отношения между вами (пользователем) и сервисом доставки 911Cargo (далее — «Сервис»).',
        'Используя наш Сервис, вы подтверждаете, что вам исполнилось 18 лет и вы обладаете правом заключать юридически обязывающие соглашения.'
      ]
    },
    {
      id: 'registration',
      title: 'Регистрация и использование аккаунта',
      icon: <Users className="w-6 h-6" />,
      content: [
        'Для оформления заказов необходимо пройти регистрацию, указав достоверные личные данные.',
        'Вы несёте ответственность за сохранность данных своей учётной записи.',
        'Мы имеем право приостанавливать или удалять аккаунт в случае нарушения настоящих условий.'
      ]
    },
    {
      id: 'orders',
      title: 'Оформление заказов',
      icon: <FileText className="w-6 h-6" />,
      content: [
        'Вы можете использовать Сервис для оформления доставки грузов от имени себя или третьих лиц.',
        'Пользователь обязан предоставлять точные данные получателя и адреса доставки.',
        'Мы не несем ответственности за ошибки, допущенные пользователем при оформлении заказа.'
      ]
    },
    {
      id: 'privacy',
      title: 'Конфиденциальность',
      icon: <Scale className="w-6 h-6" />,
      content: [
        'Мы уважаем вашу конфиденциальность. Подробности указаны в нашей Политике конфиденциальности.',
        'При регистрации и оформлении заказов мы собираем и обрабатываем ваши персональные данные согласно действующему законодательству РФ.'
      ]
    },
    {
      id: 'liability',
      title: 'Ограничение ответственности',
      icon: <AlertTriangle className="w-6 h-6" />,
      content: [
        'Мы прилагаем все усилия для бесперебойной работы сервиса, но не можем гарантировать его доступность в любое время.',
        'Мы не несём ответственности за ущерб, вызванный техническими сбоями, действиями третьих лиц или неверно предоставленной информацией.'
      ]
    },
    {
      id: 'changes',
      title: 'Изменения условий',
      icon: <RefreshCw className="w-6 h-6" />,
      content: [
        'Мы оставляем за собой право изменять настоящие Условия в любое время без предварительного уведомления.',
        'Актуальная версия всегда доступна на нашем сайте.'
      ]
    },
    {
      id: 'contacts',
      title: 'Контакты',
      icon: <Mail className="w-6 h-6" />,
      content: [
        'Email: baoxiang.tong@yandex.ru',
        'Телефон: +8615217711911',
        'Дата последнего обновления: 19 апреля 2025 г.'
      ]
    }
  ];

  const toggleSection = (id: string) => {
    setExpandedSection(expandedSection === id ? null : id);
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#fbfbfd] to-white pt-32 px-6">
      <div className="max-w-[780px] mx-auto">
        {/* Header */}
        <div className="text-center mb-16 animate-fade-in">
          <div className="inline-block p-4 bg-[#1C1F7E]/10 rounded-full mb-6">
            <Scale className="w-12 h-12 text-[#1C1F7E]" />
          </div>
          <h1 className="text-4xl md:text-5xl font-semibold text-[#1d1d1f] mb-6">
            Условия использования
          </h1>
          <p className="text-xl text-[#86868b]">
            Пожалуйста, внимательно ознакомьтесь с условиями использования нашего сервиса
          </p>
        </div>

        {/* Sections */}
        <div className="space-y-4">
          {sections.map((section, index) => (
            <div
              key={section.id}
              className={`bg-white rounded-2xl shadow-sm overflow-hidden transition-all duration-300 animate-fade-in`}
              style={{ animationDelay: `${index * 100}ms` }}
            >
              <button
                onClick={() => toggleSection(section.id)}
                className="w-full px-6 py-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
              >
                <div className="flex items-center">
                  <div className="mr-4 text-[#1C1F7E]">
                    {section.icon}
                  </div>
                  <span className="font-medium text-[#1d1d1f]">{section.title}</span>
                </div>
                <ChevronDown
                  className={`w-5 h-5 text-[#86868b] transition-transform duration-300 ${
                    expandedSection === section.id ? 'rotate-180' : ''
                  }`}
                />
              </button>
              <div
                className={`px-6 overflow-hidden transition-all duration-300 ${
                  expandedSection === section.id ? 'max-h-[500px] py-4' : 'max-h-0'
                }`}
              >
                {Array.isArray(section.content) ? (
                  <ul className="space-y-4 text-[#86868b]">
                    {section.content.map((item, i) => (
                      <li key={i}>{item}</li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-[#86868b]">{section.content}</p>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Contact Section */}
        <div className="mt-12 text-center animate-fade-in" style={{ animationDelay: '800ms' }}>
          <p className="text-[#86868b] mb-4">
            Остались вопросы по условиям использования?
          </p>
          <a
            href="/contacts"
            className="inline-flex items-center text-[#1C1F7E] hover:text-[#161856] transition-colors"
          >
            Свяжитесь с нами
            <ChevronDown className="w-5 h-5 ml-1 rotate-[-90deg]" />
          </a>
        </div>
      </div>

      <style>{`
        .animate-fade-in {
          opacity: 0;
          transform: translateY(20px);
          animation: fade-in 0.5s ease-out forwards;
        }

        @keyframes fade-in {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </div>
  );
}

export default TermsOfService;